import type { ChronologyEvent } from "~/api/model";
import { formatChronologyEventDate } from "./chronologyRequest";

/**
 * Formats chronology events as a plain-text itemized list for clipboard copy.
 *
 * Each line reads: `- DATE — DETAIL (TYPE · PROVIDER · FACILITY) [CATEGORY] — Source`
 * Empty fields are omitted so lines stay compact.
 */
export function formatChronologyAsPlainText(events: ChronologyEvent[]): string {
  if (events.length === 0) return "";
  return events.map(formatEventLine).join("\n");
}

function collapseNewlines(value: string): string {
  return value.replace(/\r?\n/g, " ").trim();
}

function formatEventLine(event: ChronologyEvent): string {
  const date = collapseNewlines(formatChronologyEventDate(event) ?? "No date");
  const detail = collapseNewlines(event.detail);

  const meta = [event.eventType, event.provider, event.facility]
    .map(collapseNewlines)
    .filter((v) => v.length > 0);

  const docName = collapseNewlines(event.documentName);
  const category = collapseNewlines(event.documentCategory);
  const source = formatSource(event);

  let line = `- ${date}`;

  if (detail.length > 0) {
    line += ` — ${detail}`;
  }

  if (meta.length > 0) {
    line += ` (${meta.join(" · ")})`;
  }

  if (docName.length > 0) {
    line += ` {${docName}}`;
  }

  if (category.length > 0) {
    line += ` [${category}]`;
  }

  if (source.length > 0) {
    line += ` — ${source}`;
  }

  return line;
}

function formatSource(event: ChronologyEvent): string {
  const { pageNumber, batesNumber } = event.reference;
  const displayPage = pageNumber != null ? pageNumber + 1 : null;
  if (displayPage != null && batesNumber) {
    return `Page ${displayPage} · ${batesNumber}`;
  }
  if (displayPage != null) {
    return `Page ${displayPage}`;
  }
  if (batesNumber) {
    return batesNumber;
  }
  return "";
}
